/**
 * React Query hooks for CRM tasks.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { CrmCreateTaskInput, CrmTask } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

interface UseTasksOptions {
  contactId?: string;
  companyId?: string;
  dealId?: string;
  status?: string;
  priority?: string;
  overdue?: boolean;
  sortBy?: string;
  sortDir?: string;
  limit?: number;
}

function buildParams(options: UseTasksOptions): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  if (options.contactId) params.contactId = options.contactId;
  if (options.companyId) params.companyId = options.companyId;
  if (options.dealId) params.dealId = options.dealId;
  if (options.status) params.status = options.status;
  if (options.priority) params.priority = options.priority;
  if (options.overdue) params.overdue = true;
  if (options.sortBy) params.sortBy = options.sortBy;
  if (options.sortDir) params.sortDir = options.sortDir;
  if (options.limit !== undefined) params.limit = options.limit;
  return params;
}

export function useTasks(options: UseTasksOptions = {}) {
  const queryClient = useQueryClient();
  const filters = buildParams(options);

  const query = useQuery<{ tasks: CrmTask[]; total: number }>({
    queryKey: queryKeys.crm.tasks.list(filters),
    queryFn: () => api.listCrmTasks(Object.keys(filters).length > 0 ? filters : undefined),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.crm.tasks.all() });

  const createMutation = useMutation({
    mutationFn: (input: CrmCreateTaskInput) => api.createCrmTask(input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to create task', variant: 'destructive' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<CrmCreateTaskInput> }) =>
      api.updateCrmTask(id, input),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to update task', variant: 'destructive' }),
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) => api.completeCrmTask(id),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to complete task', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteCrmTask(id),
    onSuccess: invalidate,
    onError: () =>
      toast({ title: 'Error', description: 'Failed to delete task', variant: 'destructive' }),
  });

  if (query.isError && !query.isFetching) {
    toast({ title: 'Error', description: 'Failed to load tasks', variant: 'destructive' });
  }

  return {
    tasks: query.data?.tasks ?? [],
    total: query.data?.total ?? 0,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
    createTask: (input: CrmCreateTaskInput) => createMutation.mutateAsync(input),
    updateTask: (id: string, input: Partial<CrmCreateTaskInput>) =>
      updateMutation.mutateAsync({ id, input }),
    completeTask: (id: string) => completeMutation.mutateAsync(id),
    deleteTask: (id: string) => deleteMutation.mutateAsync(id),
  };
}
